import { useEffect, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion, type PanInfo } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { MenuGroup } from './types'

interface WeeklyMenuSectionProps {
  id?: string
  title: string
  description?: string
  groups: MenuGroup[]
}

export function WeeklyMenuSection({
  id,
  title,
  description,
  groups,
}: WeeklyMenuSectionProps) {
  const shouldReduceMotion = useReducedMotion()
  const allItems = groups.flatMap((g) => g.items)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isHovering, setIsHovering] = useState(false)

  useEffect(() => {
    if (isHovering || allItems.length === 0) return
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % allItems.length)
    }, 4200)
    return () => clearInterval(interval)
  }, [isHovering, allItems.length])

  const activeItem = allItems[activeIndex]

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const handleActivate = (index: number) => {
    setIsHovering(true)
    setActiveIndex(index)
  }

  const handleDeactivate = () => setIsHovering(false)

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const offsetThreshold = 50
    const velocityThreshold = 500
    if (info.offset.x > offsetThreshold || info.velocity.x > velocityThreshold) {
      setActiveIndex((prev) => (prev === 0 ? allItems.length - 1 : prev - 1))
    } else if (info.offset.x < -offsetThreshold || info.velocity.x < -velocityThreshold) {
      setActiveIndex((prev) => (prev + 1) % allItems.length)
    }
  }

  return (
    <section
      id={id}
      className="bg-cream px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8"
      aria-labelledby={`${id}-heading`}
    >
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-12 text-center sm:mb-16">
          <h2
            id={`${id}-heading`}
            className="font-display text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold leading-[0.95] tracking-tighter text-ink"
          >
            {title}
          </h2>
          {description && (
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">{description}</p>
          )}
        </motion.div>

        <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Featured image */}
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="relative mx-auto max-w-sm lg:max-w-none">
                <div
                  className="absolute inset-0 rotate-2 rounded-[2rem] bg-cord"
                  aria-hidden="true"
                />
                <div className="relative -rotate-1 rounded-[2rem] border-[6px] border-white bg-white p-2 shadow-xl">
                  <motion.div
                    className="relative aspect-[4/5] w-full overflow-hidden rounded-[1.5rem] bg-silver"
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.2}
                    onDragStart={() => setIsHovering(true)}
                    onDragEnd={(e, info) => {
                      handleDragEnd(e, info)
                      setIsHovering(false)
                    }}
                  >
                    {activeItem?.image && (
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={activeIndex}
                          src={activeItem.image}
                          alt={activeItem.title}
                          initial={{ opacity: 0, scale: 1.05 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: shouldReduceMotion ? 0 : 0.4 }}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </AnimatePresence>
                    )}
                  </motion.div>
                </div>
              </div>

              {/* Dots */}
              <div className="mt-5 flex justify-center gap-2">
                {allItems.map((item, index) => (
                  <button
                    key={item.id ?? item.title}
                    type="button"
                    aria-label={item.title}
                    aria-current={index === activeIndex ? 'true' : undefined}
                    onClick={() => handleActivate(index)}
                    onMouseEnter={() => handleActivate(index)}
                    onMouseLeave={handleDeactivate}
                    className={cn(
                      'h-2.5 w-2.5 rounded-full transition-colors',
                      index === activeIndex
                        ? 'bg-maia'
                        : 'bg-cord hover:bg-maia/50'
                    )}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Menu groups */}
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="grid gap-10 sm:grid-cols-2">
              {groups.map((group) => (
                <div key={group.title} className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-widest text-muted">
                    {group.title}
                  </h3>

                  <div className="space-y-3">
                    {group.items.map((item) => {
                      const flatIndex = allItems.findIndex((i) => i === item)
                      const isActive = activeIndex === flatIndex
                      return (
                        <button
                          key={item.id ?? item.title}
                          type="button"
                          onMouseEnter={() => handleActivate(flatIndex)}
                          onMouseLeave={handleDeactivate}
                          onFocus={() => handleActivate(flatIndex)}
                          onBlur={handleDeactivate}
                          onClick={() => handleActivate(flatIndex)}
                          className={cn(
                            'group w-full rounded-xl border-l-4 bg-white p-4 text-left shadow-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-maia focus-visible:ring-offset-2 sm:p-5',
                            isActive
                              ? 'border-maia shadow-md'
                              : 'border-cord hover:border-maia/60 hover:shadow-md'
                          )}
                        >
                          <span className="flex flex-col">
                            <span className="font-display text-lg font-semibold leading-tight tracking-tight text-ink sm:text-xl">
                              {item.title}
                            </span>
                            {item.description && (
                              <span className="mt-1 text-sm leading-relaxed text-muted">
                                {item.description}
                              </span>
                            )}
                            {item.price && (
                              <span className="mt-2 inline-block rounded-full bg-maia/10 px-2.5 py-1 text-xs font-bold text-maia-dark">
                                {item.price}
                              </span>
                            )}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
